import { Archive, ArchiveRestore, Pencil, Plus } from 'lucide-react';
import { Badge, Button, PageSectionCard, Skeleton, toast } from '@kmgestao/ui';

import {
  useArchivePlan,
  usePlansAdmin,
  useUnarchivePlan,
  type PlanRow,
} from '../../../../features/plans-admin/index.js';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** Lista todos os planos (ativos + arquivados) em cards compactos.
 * Arquivado aparece com opacidade reduzida + badge "arquivado" pra
 * distinguir visualmente. */
export function PlansList({
  onEdit,
  onAdd,
}: {
  onEdit: (plan: PlanRow) => void;
  onAdd: () => void;
}) {
  const query = usePlansAdmin();
  const archiveMutation = useArchivePlan();
  const unarchiveMutation = useUnarchivePlan();

  if (query.isLoading) {
    return (
      <PageSectionCard title="Planos da plataforma">
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      </PageSectionCard>
    );
  }

  if (query.isError) {
    return (
      <PageSectionCard title="Planos da plataforma">
        <p className="text-sm text-destructive">
          Erro ao carregar planos:{' '}
          {query.error instanceof Error ? query.error.message : 'desconhecido'}
        </p>
      </PageSectionCard>
    );
  }

  const plans = query.data ?? [];
  const handleArchive = async (plan: PlanRow) => {
    try {
      await archiveMutation.mutateAsync({ key: plan.key });
      toast.success(`Plano "${plan.name_pt_br}" arquivado.`);
    } catch (err) {
      toast.error('Não foi possível arquivar', {
        description: err instanceof Error ? err.message : '',
      });
    }
  };
  const handleUnarchive = async (plan: PlanRow) => {
    try {
      await unarchiveMutation.mutateAsync({ key: plan.key });
      toast.success(`Plano "${plan.name_pt_br}" reativado.`);
    } catch (err) {
      toast.error('Não foi possível reativar', {
        description: err instanceof Error ? err.message : '',
      });
    }
  };

  return (
    <PageSectionCard
      title="Planos da plataforma"
      description="Cadastro base de planos oferecidos. Alterações refletem em novas propostas e no catálogo público."
      action={
        <Button type="button" size="sm" onClick={onAdd}>
          <Plus aria-hidden="true" className="mr-1 size-4" />
          Novo plano
        </Button>
      }
    >
      {plans.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum plano cadastrado. Clique em &ldquo;Novo plano&rdquo; pra criar o primeiro.
        </p>
      ) : (
        <ul className="space-y-3">
          {plans.map((plan) => (
            <PlanRowCard
              key={plan.id}
              plan={plan}
              onEdit={() => onEdit(plan)}
              onArchive={() => handleArchive(plan)}
              onUnarchive={() => handleUnarchive(plan)}
              isPending={archiveMutation.isPending || unarchiveMutation.isPending}
            />
          ))}
        </ul>
      )}
    </PageSectionCard>
  );
}

function PlanRowCard({
  plan,
  onEdit,
  onArchive,
  onUnarchive,
  isPending,
}: {
  plan: PlanRow;
  onEdit: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  isPending: boolean;
}) {
  const isArchived = !plan.is_active;
  return (
    <li
      className={`rounded-lg border border-border p-4 transition-opacity ${
        isArchived ? 'bg-muted/10 opacity-60' : 'bg-card'
      }`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-foreground">{plan.name_pt_br}</h3>
            <Badge variant="outline" className="font-mono text-xs">
              {plan.key}
            </Badge>
            <Badge variant="secondary">{plan.tier}</Badge>
            {isArchived ? <Badge variant="destructive">arquivado</Badge> : null}
            {!plan.is_public && !isArchived ? <Badge variant="outline">oculto</Badge> : null}
          </div>
          {plan.description ? (
            <p className="text-sm text-muted-foreground">{plan.description}</p>
          ) : null}
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <div>
              <dt className="inline font-medium text-foreground">Mensal:</dt>{' '}
              <dd className="inline">
                {plan.monthly_price_brl === null
                  ? 'sob consulta'
                  : BRL.format(plan.monthly_price_brl)}
              </dd>
            </div>
            {plan.yearly_price_brl !== null ? (
              <div>
                <dt className="inline font-medium text-foreground">Anual:</dt>{' '}
                <dd className="inline">{BRL.format(plan.yearly_price_brl)}</dd>
              </div>
            ) : null}
            <div>
              <dt className="inline font-medium text-foreground">Trial:</dt>{' '}
              <dd className="inline">{plan.trial_days}d</dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">Users:</dt>{' '}
              <dd className="inline">{plan.max_users ?? '∞'}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">Pacientes:</dt>{' '}
              <dd className="inline">{plan.max_patients ?? '∞'}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">Ciclo:</dt>{' '}
              <dd className="inline">{plan.billing_cycle === 'yearly' ? 'Anual' : 'Mensal'}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">Pgto Asaas:</dt>{' '}
              <dd className="inline">
                {plan.asaas_default_billing_type === 'UNDEFINED'
                  ? 'cliente escolhe'
                  : plan.asaas_default_billing_type.toLowerCase()}
              </dd>
            </div>
          </dl>
          {plan.features.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {plan.features.map((feat) => (
                <Badge key={feat} variant="outline" className="text-xs font-normal">
                  {feat}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit} disabled={isPending}>
            <Pencil aria-hidden="true" className="mr-1 size-4" />
            Editar
          </Button>
          {isArchived ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onUnarchive}
              disabled={isPending}
            >
              <ArchiveRestore aria-hidden="true" className="mr-1 size-4" />
              Reativar
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onArchive}
              disabled={isPending}
            >
              <Archive aria-hidden="true" className="mr-1 size-4" />
              Arquivar
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
